/* AppraisalComponents/hooks/useRepairsRowCascade.js
 * Phase C · C4g (2026-02c) — Row-scoped repairs material carry-through.
 *
 * Fires whenever `groups` changes and stamps `material = engineMaterial`
 * on any row that lacks a material, across every material-aware section.
 * Uses the same row-scoped engine pattern proven with Flashing (C3c-δ):
 * appraisal-level engine result → applied to each individual row via
 * a setGroups update, with per-section rate-catalogue gate so we never
 * stamp a material a section can't actually price.
 *
 * Contract:
 *   • Fires only when quoteType matches a repair-family regex OR the
 *     engine emits a material result from R-D-007 (service_option
 *     in [Repairs, Maintenance, Re-Coat]).
 *   • Never touches rows with `override === true` (operator sacred).
 *   • Never touches rows that already have a material set.
 *   • Per-section gate: only stamps `material` if the section's rate
 *     catalogue has ≥1 row that matches it (via detectMaterialFor).
 *     Prevents stamping "Galv" on a Barge section that only stocks
 *     Coloursteel rates → dead dropdown.
 *
 * Extracted from the (previously ONLY) load-time material stamp in
 * AppraisalComponentsTab.js `load()`. Now runs at load AND on every
 * subsequent addLine / diagram-push / duplicate-last.
 */
import { useEffect } from 'react';
import { MATERIAL_SECTIONS, rateHasMaterial } from '@/components/shared/ComponentRow/helpers';

// Quote-type patterns that imply "carry the existing roof material
// through". Mirrors the engine's QTM ladder (rescrew|repair|
// restoration|maintenance) so the frontend gate matches the backend
// rule that produced engineMaterial.
const REPAIR_FAMILY_RE = /rescrew|repair|restoration|maintenance/i;

export function useRepairsRowCascade({
  loading,
  quoteType,
  engineMaterial,
  rates,
  groups,
  setGroups,
}) {
  useEffect(() => {
    if (loading) return;
    if (!engineMaterial) return;
    // Fire only for repair-family quote types so we don't accidentally
    // stamp material during a fresh Re-roof flow (that has its own
    // section-level material picker workflow).
    if (!REPAIR_FAMILY_RE.test(String(quoteType || ''))) return;

    setGroups(prev => {
      let touched = false;
      const next = { ...prev };
      for (const section of MATERIAL_SECTIONS) {
        const lines = prev[section] || [];
        if (!lines.length) continue;
        const sectionRates = rates[section] || [];
        if (!sectionRates.length) continue;
        // Per-section gate: does this section stock the material?
        const sectionSupports = sectionRates.some(
          r => rateHasMaterial(section, r, engineMaterial),
        );
        if (!sectionSupports) continue;

        let sectionTouched = false;
        const nextLines = lines.map(l => {
          if (l.override) return l;
          if (l.material) return l;  // already set · never override
          sectionTouched = true;
          return { ...l, material: engineMaterial };
        });
        if (sectionTouched) {
          next[section] = nextLines;
          touched = true;
        }
      }
      return touched ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, quoteType, engineMaterial, rates, groups]);
}
